'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "groupKey", "groupOptions", "onChange", "selectedOptions", "translateOption", "searchTerm"],
  _excluded2 = ["groupKey", "groupOptions", "onChange", "searchTerm", "selectedOptions", "translateOption"];
import { Fragment, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISearchInput from 'UIComponents/input/UISearchInput';
import NestedNav from 'ecosystem-marketplace-core/components/nav/NestedNav';
import NestedNavCheckboxItem from '../../sidebar/inputs/NestedNavCheckboxItem';
import UnstyledNestedNavItem from '../../sidebar/inputs/UnstyledNestedNavItem';
import ExpandableFilter from '../../sidebar/filters/ExpandableFilter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getOpenIcon = open => open ? 'up' : 'down';
const FullySelectableNestedCheckboxGroup = _ref => {
  let {
      disabled,
      groupKey,
      groupOptions,
      onChange,
      selectedOptions,
      translateOption,
      searchTerm
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const allSelected = useMemo(() => groupOptions.every(option => selectedOptions.includes(option)), [groupOptions, selectedOptions]);
  const someSelected = useMemo(() => groupOptions.some(option => selectedOptions.includes(option)), [groupOptions, selectedOptions]);
  const renderedChildOptions = useMemo(() => groupOptions.sortBy(option => option).filter(option => {
    if (searchTerm) {
      const searchRegex = new RegExp(searchTerm, 'i');
      return searchRegex.test(translateOption(option));
    }
    return true;
  }).map(option => /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
    checkboxProps: {
      checked: selectedOptions.includes(option),
      disabled,
      onChange: () => {
        onChange(option);
      }
    },
    title: translateOption(option)
  }, option)).toArray(), [disabled, groupOptions, onChange, searchTerm, selectedOptions, translateOption]);
  if (renderedChildOptions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(NestedNavCheckboxItem, Object.assign({}, rest, {
    checkboxProps: {
      checked: allSelected,
      disabled,
      indeterminate: someSelected,
      onChange: () => {
        onChange(groupOptions, allSelected);
      },
      value: groupKey
    },
    defaultOpen: allSelected || someSelected,
    title: groupKey,
    children: renderedChildOptions
  }));
};
const NestedCheckboxGroup = _ref2 => {
  let {
      groupKey,
      groupOptions,
      onChange,
      searchTerm,
      selectedOptions,
      translateOption
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const [open, setOpen] = useState(false);
  const renderedOptions = useMemo(() => {
    return groupOptions.filter(option => {
      if (searchTerm) {
        const searchRegex = new RegExp(searchTerm, 'i');
        return searchRegex.test(translateOption(option));
      }
      return true;
    }).sortBy(option => option).map(option => /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
      checkboxProps: {
        checked: selectedOptions.includes(option),
        onChange: () => {
          onChange(option);
        }
      },
      title: translateOption(option)
    }, option));
  }, [groupOptions, onChange, searchTerm, selectedOptions, translateOption]);
  if (renderedOptions.isEmpty()) {
    return null;
  }
  return /*#__PURE__*/_jsx(UnstyledNestedNavItem, Object.assign({}, rest, {
    iconRight: searchTerm ? null : getOpenIcon(open),
    open: open || !!searchTerm,
    onOpenChange: ({
      target: {
        value
      }
    }) => {
      setOpen(value);
    },
    title: groupKey,
    children: renderedOptions
  }));
};
const MobileCheckboxGroup = ({
  defaultDisplayCount,
  fullySelectable,
  name,
  onChange,
  options,
  searchEnabled,
  searchPlaceholder,
  translateOption,
  value
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const isSingleSelect = typeof value === 'boolean';
  const handleChange = useCallback((inputValue, selectedAll) => {
    if (isSingleSelect) {
      onChange(!value);
    } else if (typeof inputValue === typeof Immutable.Set()) {
      let tempValue = value;
      inputValue.forEach(input => {
        if (selectedAll) {
          if (value.includes(input)) {
            tempValue = tempValue.delete(input);
          }
        } else {
          if (!value.includes(input)) {
            tempValue = tempValue.add(input);
          }
        }
        onChange(tempValue);
      });
    } else {
      onChange(value.includes(inputValue) ? value.delete(inputValue) : value.add(inputValue));
    }
  }, [isSingleSelect, onChange, value]);
  const renderedInputs = useMemo(() => {
    let tempOptions = {};
    options.forEach((nestedOptions, key) => tempOptions = Object.assign({}, tempOptions, {
      [key]: nestedOptions
    }));
    const optionsMap = Immutable.OrderedMap(tempOptions);
    const renderedIndividualInputs = [];
    let sortable = true;
    optionsMap.forEach((option, key) => {
      if (typeof option === typeof Immutable.Set()) {
        sortable = false;
        if (fullySelectable) {
          renderedIndividualInputs.push( /*#__PURE__*/_jsx(FullySelectableNestedCheckboxGroup, {
            groupKey: key,
            groupOptions: option,
            onChange: (selectedValue, allSelected = false) => handleChange(selectedValue, allSelected),
            searchTerm: searchTerm,
            selectedOptions: value,
            translateOption: translateOption
          }, key));
        } else {
          renderedIndividualInputs.push( /*#__PURE__*/_jsx(NestedCheckboxGroup, {
            groupKey: key,
            groupOptions: option,
            onChange: (selectedValue, allSelected = false) => handleChange(selectedValue, allSelected),
            searchTerm: searchTerm,
            selectedOptions: value,
            translateOption: translateOption
          }, key));
        }
      } else {
        const searchRegex = new RegExp(searchTerm, 'i');
        if (!searchTerm || searchRegex.test(translateOption(option))) {
          renderedIndividualInputs.push( /*#__PURE__*/_jsx(NestedNavCheckboxItem, {
            checkboxProps: {
              checked: isSingleSelect ? value : value.includes(option),
              onChange: () => handleChange(option)
            },
            title: translateOption(option)
          }, option));
        }
      }
    });
    return sortable ? renderedIndividualInputs.sort((lang1, lang2) => {
      if (lang1.props.title.toLowerCase() > lang2.props.title.toLowerCase()) {
        return 1;
      }
      return -1;
    }) : renderedIndividualInputs;
  }, [fullySelectable, handleChange, isSingleSelect, options, searchTerm, translateOption, value]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [searchEnabled && /*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": name,
      children: /*#__PURE__*/_jsx(UISearchInput, {
        onChange: ({
          target: {
            value: searchValue
          }
        }) => {
          if (searchValue.trim()) {
            setSearchTerm(searchValue);
          } else {
            setSearchTerm('');
          }
        },
        placeholder: searchPlaceholder
      })
    }), /*#__PURE__*/_jsx(NestedNav, {
      children: defaultDisplayCount ? /*#__PURE__*/_jsx(ExpandableFilter, {
        name: name,
        defaultDisplayCount: defaultDisplayCount,
        children: Immutable.OrderedSet(renderedInputs)
      }) : Immutable.OrderedSet(renderedInputs)
    })]
  });
};
MobileCheckboxGroup.propTypes = {
  defaultDisplayCount: PropTypes.number,
  fullySelectable: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.oneOfType([PropTypes.instanceOf(Immutable.Set), PropTypes.instanceOf(Immutable.Map), PropTypes.array]).isRequired,
  searchEnabled: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  translateOption: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Immutable.Set), PropTypes.bool]).isRequired
};
export default MobileCheckboxGroup;